import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@bookdental/ui";
import type { IncomingOrderRow } from "./production-types";

type Props = {
  order: IncomingOrderRow | null;
  onClose: () => void;
  onConfirm: (reason: string | undefined) => void;
  isPending: boolean;
};

/** Confirmação de recusa de pedido — motivo é opcional (sugerido, não obrigatório), fica
 *  visível pro dentista depois no detalhe do pedido dele. */
export function DeclineOrderDialog({ order, onClose, onConfirm, isPending }: Props) {
  const [reason, setReason] = useState("");

  function handleClose() {
    setReason("");
    onClose();
  }

  function handleConfirm() {
    onConfirm(reason.trim() || undefined);
    setReason("");
  }

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && !isPending && handleClose()}>
      <DialogContent className="max-w-[360px] p-4" overlayClassName="backdrop-blur-none">
        {order && (
          <>
            <div className="flex items-start gap-2.5">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400">
                <AlertTriangle className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="text-sm font-semibold text-zinc-900 dark:text-foreground">
                  Recusar pedido?
                </DialogTitle>
                <p className="mt-1 text-xs leading-relaxed text-zinc-600 dark:text-muted-foreground">
                  O pedido de "{order.patientName}" será marcado como recusado. Essa ação não tem volta fácil.
                </p>
              </div>
            </div>

            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground">
                Motivo (opcional)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Ex: faltam informações do caso, prazo incompatível..."
                className="w-full resize-none rounded-md border border-zinc-200 bg-white px-2.5 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
              />
              <p className="mt-1 text-[11px] text-zinc-400 dark:text-muted-foreground/70">
                Se preenchido, o dentista vê esse motivo no pedido.
              </p>
            </div>

            <div className="mt-3 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isPending}
                className="inline-flex h-8 flex-1 items-center justify-center rounded-md border border-zinc-200 bg-white px-3 text-xs font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 dark:border-border dark:bg-card dark:text-foreground"
              >
                Voltar
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isPending}
                className="inline-flex h-8 flex-1 items-center justify-center rounded-md bg-red-600 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                Recusar pedido
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
